import { groupBy, mapValues, sample } from 'lodash-es'
import { invariant } from '#src/invariant.ts'
import { type IDTRole, isIDTRole } from '#src/models/care-teams/constants.ts'
import type { ExtendedPractitioner } from '#src/models/practitioner.ts'
import { Role } from '#src/roles.ts'
import { recordEntries } from '#src/utils.ts'

// groups practitioners by role instead of roles
export function groupPractitionersByRole(
  practitioners: ExtendedPractitioner[]
): Record<Role, ExtendedPractitioner[]> {
  const groupByRole = groupBy(
    practitioners.flatMap(
      (p) => p.roles?.map((role) => ({ role, practitioner: p })) ?? []
    ),
    ({ role }) => role
  )

  return mapValues(groupByRole, (group) =>
    group.map((p) => p.practitioner)
  ) as Record<Role, ExtendedPractitioner[]>
}

export function samplePractitionerByIDTRole(
  practitionersByRole: Record<Role, ExtendedPractitioner[]>,
  isProd: boolean
): { role: IDTRole; practitioner: ExtendedPractitioner }[] {
  const excludedPcpEmails = [
    'victor.lee@seensgv.com',
    'otashe.golden@seensgv.com',
  ]
  return recordEntries(practitionersByRole)
    .filter(([role]) => isIDTRole(role))
    .map(([role, practitioners]) => {
      let filteredPractitioners = practitioners
      if (isProd) {
        if (role === Role.PCP) {
          filteredPractitioners = practitioners.filter(
            (p) => !excludedPcpEmails.includes(p.primaryEmail ?? '')
          )
        } else if (role === Role.TR) {
          filteredPractitioners = practitioners.filter(
            (p) => p.primaryEmail !== 'jing.wang@seensgv.com'
          )
        } else if (role === Role.RD) {
          filteredPractitioners = practitioners.filter(
            (p) => p.primaryEmail === 'camilia.lee@seensgv.com'
          )
        } else if (role === Role.PT) {
          filteredPractitioners = practitioners.filter(
            (p) => p.primaryEmail === 'kyle.yeh@seensgv.com'
          )
        } else if (role === Role.CM) {
          filteredPractitioners = practitioners.filter(
            (p) => p.primaryEmail === 'steve@seenhealth.org'
          )
        }
      }
      const practitioner = sample(filteredPractitioners)
      invariant(practitioner, `Practitioner is required for ${role}`)
      return {
        practitioner,
        // filter above guarantees that role is an IDTRole
        role: role as IDTRole,
      }
    })
}
